import * as Yup from "yup";
import { DEFAULT_DYNAMIC_FORM_ENGINE_CONFIG } from "../context/DynamicFormEngineConfigContext";
import {
  IFormSchemaField,
  IFormSchemaSection,
  IFormValidationRule
} from "../types";
import { isFieldDisabled, isHiddenUiField } from "./fieldRules";
import { getAttestationText, getJurisdictionCountry } from "./formMetadata";

/**
 * Host-supplied validation hooks (see DynamicFormEngineConfig). The engine has
 * no domain knowledge of what a MaskedId value IS — the host layers its format
 * check (e.g. the tax layer's SSN/ITIN validator) on top of the schema rules.
 */
export type MaskedIdValidator = (
  field: IFormSchemaField,
  country: string
) => Yup.StringSchema;

const hasRequired = (rules: IFormValidationRule[]): boolean =>
  rules.some(r => r.kind === "Required");

/**
 * Applies the RequiredIf / RequiredUnless conditional-required rules on top of an
 * already-built schema. Both reference another field (`ref`) and a value
 * (`equals`); RequiredIf makes the field required when the ref matches, while
 * RequiredUnless makes it required when the ref does NOT match.
 */
const applyConditionalRequired = (
  schema: Yup.AnySchema,
  rules: IFormValidationRule[]
): Yup.AnySchema => {
  let next = schema;
  rules.forEach(rule => {
    if (rule.kind === "RequiredIf") {
      next = next.when(rule.ref, {
        is: (value: unknown) => value === rule.equals,
        then: (s: Yup.AnySchema) =>
          s.required(rule.message ?? "This field is required")
      });
    }
    if (rule.kind === "RequiredUnless") {
      next = next.when(rule.ref, {
        is: (value: unknown) => value !== rule.equals,
        then: (s: Yup.AnySchema) =>
          s.required(rule.message ?? "This field is required")
      });
    }
  });
  return next;
};

const buildStringSchema = (
  field: IFormSchemaField,
  rules: IFormValidationRule[],
  baseSchema?: Yup.StringSchema
): Yup.AnySchema => {
  // `.nullable()` because clearing a WSSelect (its built-in ✕ action) sets the
  // RHF value to null, and yup 0.32 runs the string TYPE check before any
  // whitelist — without nullable the user gets a raw, unfixable type error.
  // Presence stays governed solely by the Required rule (which rejects null
  // and "" alike).
  let schema = (baseSchema ?? Yup.string()).nullable();
  rules.forEach(rule => {
    switch (rule.kind) {
      case "Required":
        schema = schema.required(rule.message ?? `${field.label} is required`);
        break;
      // Both membership rules admit the empty seed ("" / null / undefined):
      // on yup 0.32 `oneOf` rejects them, which would flag an untouched
      // OPTIONAL dropdown as "Invalid value" with no way to clear it.
      case "OneOf":
        schema = schema.oneOf(
          [...(rule.values as string[]), "", null, undefined],
          rule.message ?? "Invalid value"
        );
        break;
      case "AllowedValue":
        schema = schema.oneOf(
          [rule.value as string, "", null, undefined],
          rule.message ?? "Invalid value"
        );
        break;
      default:
        break;
    }
  });
  return applyConditionalRequired(schema, rules);
};

const buildNumberSchema = (
  field: IFormSchemaField,
  rules: IFormValidationRule[]
): Yup.AnySchema => {
  let schema = Yup.number().typeError("Must be a number");
  rules.forEach(rule => {
    switch (rule.kind) {
      case "Required":
        schema = schema.required(rule.message ?? `${field.label} is required`);
        break;
      case "MinValue":
        schema = schema.min(
          rule.value,
          rule.message ?? `Must be at least ${rule.value}`
        );
        break;
      case "MaxValue":
        schema = schema.max(
          rule.value,
          rule.message ?? `Must be at most ${rule.value}`
        );
        break;
      default:
        break;
    }
  });
  return applyConditionalRequired(schema.nullable(), rules);
};

const buildBooleanSchema = (
  field: IFormSchemaField,
  rules: IFormValidationRule[]
): Yup.AnySchema => {
  let schema = Yup.boolean();
  if (hasRequired(rules)) {
    const message =
      rules.find(r => r.kind === "Required")?.message ??
      `${field.label} is required`;
    schema = schema.oneOf([true], message).required(message);
  }
  return schema;
};

const buildEnumSchema = (
  field: IFormSchemaField,
  rules: IFormValidationRule[]
): Yup.AnySchema => {
  if (field.multi) {
    let schema = Yup.array().of(Yup.string());
    if (hasRequired(rules)) {
      schema = schema.min(
        1,
        rules.find(r => r.kind === "Required")?.message ??
          `${field.label} is required`
      );
    }
    return schema;
  }
  return buildStringSchema(field, rules);
};

const buildAddressSchema = (
  rules: IFormValidationRule[],
  country: string
): Yup.AnySchema => {
  const required = hasRequired(rules);
  // US zips are exactly 5 digits — mirrors the shared FormPartialAddress
  // validation so the engine is never laxer than the rest of the app.
  // `excludeEmptyString` keeps the "" seed of an untouched optional address
  // from failing (empty-ness is governed by the required chain alone).
  const postalCode =
    country === "US"
      ? Yup.string().matches(/^\d{5}$/, {
          message: "Should be 5 characters",
          excludeEmptyString: true
        })
      : Yup.string();
  // The State/Province select renders with a required marker only for US/CA
  // (elsewhere the UI labels it "Region/state (optional)"), so the schema
  // requires it for exactly those jurisdictions — same as the shared
  // FormPartialAddress validation.
  const stateRequired = required && (country === "US" || country === "CA");
  return Yup.object().shape({
    addressLine1: required
      ? Yup.string().required("Street address is required")
      : Yup.string(),
    addressLine2: Yup.string(),
    city: required ? Yup.string().required("City is required") : Yup.string(),
    state: stateRequired
      ? Yup.string().nullable().required("State is required")
      : Yup.string().nullable(),
    postalCode: required
      ? postalCode.required("Postal code is required")
      : postalCode
  });
};

/**
 * Repeating group → a Yup array of per-row object schemas. Only the
 * value-shaped rules (Required / MinValue / MaxValue) are enforced client-side;
 * conditional kinds are server-authoritative. Required is additionally limited
 * to UNCONDITIONALLY visible item fields — a row-locally hidden item field is
 * unregistered (its value pruned server-side per row), so enforcing Required on
 * it would block submit with an error the user cannot see or fix.
 */
const buildGroupSchema = (
  field: IFormSchemaField,
  country: string,
  maskedIdValidator?: MaskedIdValidator
): Yup.AnySchema => {
  const item = field.item;
  const itemShape: Record<string, Yup.AnySchema> = {};
  (item?.fields ?? []).forEach(itemField => {
    if (itemField.type === "Group") return; // nested groups are engine-rejected
    // Mirror the top-level exclusions: computed / hidden-UI / disabled item
    // fields render no editable input (the auto-calculated box ignores error
    // props), so validating them could block submit with an error the user
    // can neither see nor fix.
    if (
      itemField.operations.computed != null ||
      isHiddenUiField(itemField) ||
      isFieldDisabled(itemField)
    ) {
      return;
    }
    const isConditionallyVisible =
      itemField.operations.visibleWhen !== undefined &&
      itemField.operations.visibleWhen.operator !== "Always";
    const rules = itemField.validation.rules.filter(rule =>
      rule.kind === "Required"
        ? !isConditionallyVisible
        : rule.kind === "MinValue" || rule.kind === "MaxValue"
    );
    const itemSchema = buildFieldSchema(
      { ...itemField, validation: { ...itemField.validation, rules } },
      country,
      maskedIdValidator
    );
    if (itemSchema) itemShape[itemField.key] = itemSchema;
  });

  let schema = Yup.array().of(Yup.object().shape(itemShape));
  // A `Required` rule on the Group itself means "at least one row" (when the
  // schema doesn't also carry an explicit minItems).
  if (hasRequired(field.validation.rules) && item?.minItems === undefined) {
    schema = schema.min(
      1,
      field.validation.rules.find(r => r.kind === "Required")?.message ??
        `At least 1 ${item?.itemLabel ?? "item"} required`
    );
  }
  if (item?.minItems !== undefined) {
    schema = schema.min(
      item.minItems,
      `At least ${item.minItems} ${item.itemLabel ?? "item"}${
        item.minItems === 1 ? "" : "s"
      } required`
    );
  }
  if (item?.maxItems !== undefined) {
    schema = schema.max(
      item.maxItems,
      `At most ${item.maxItems} ${item.itemLabel ?? "item"}${
        item.maxItems === 1 ? "" : "s"
      } allowed`
    );
  }
  return schema;
};

const buildFieldSchema = (
  field: IFormSchemaField,
  country: string,
  maskedIdValidator?: MaskedIdValidator
): Yup.AnySchema | undefined => {
  const rules = field.validation.rules;
  switch (field.type) {
    // MaskedId is a sensitive identifier (rendered masked — see
    // fieldTypeComponentMap). The host's maskedIdValidator (e.g. the tax
    // layer's SSN/ITIN format check) is layered on top of the string rules; it
    // skips empty values so a `Required` rule still governs presence.
    case "MaskedId":
      return buildStringSchema(
        field,
        rules,
        maskedIdValidator?.(field, country)
      );
    case "String":
      return buildStringSchema(field, rules);
    case "Number":
    case "Currency":
    case "Percent":
      return buildNumberSchema(field, rules);
    case "Boolean":
    case "SignatureBlock":
      return buildBooleanSchema(field, rules);
    case "Enum":
      return buildEnumSchema(field, rules);
    case "Date":
      return applyConditionalRequired(
        hasRequired(rules)
          ? Yup.date()
              .nullable()
              .required(
                rules.find(r => r.kind === "Required")?.message ??
                  `${field.label} is required`
              )
          : Yup.date().nullable(),
        rules
      );
    case "Address":
      return buildAddressSchema(rules, country);
    case "Group":
      return buildGroupSchema(field, country, maskedIdValidator);
    case "ReadOnly":
      return undefined;
    default:
      return undefined;
  }
};

/**
 * Builds a Yup schema from the visible fields of a forms `IGetFormSchemaResponse`.
 * Hidden fields (`operations.visible === false`) are skipped so conditionally
 * hidden inputs never block submit. SumEquals is a no-op (server-authoritative)
 * and MutuallyExclusive is best-effort (not enforced client-side).
 *
 * If the form carries an attestation (metadata.attestation.text) but no
 * SignatureBlock field already collects consent, a `signature` consent boolean
 * is required.
 */
export const catalogToYup = (params: {
  sections: IFormSchemaSection[];
  metadata?: unknown;
  /** Fallback country when metadata carries no jurisdiction. */
  defaultCountry?: string;
  /** Host validator for MaskedId fields (see DynamicFormEngineConfig). */
  maskedIdValidator?: MaskedIdValidator;
}) => {
  const { sections, metadata, maskedIdValidator } = params;
  const country = getJurisdictionCountry(
    metadata,
    params.defaultCountry ?? DEFAULT_DYNAMIC_FORM_ENGINE_CONFIG.defaultCountry
  );
  const shape: Record<string, Yup.AnySchema> = {};
  let hasSignatureField = false;

  sections.forEach(section => {
    section.fields.forEach(field => {
      if (field.operations.visible === false) return;
      // Server-computed fields render read-only ("Auto-calculated") and the user
      // cannot edit them, so client validation of their value is meaningless and
      // would block submit with an error the user can't fix (and that the
      // read-only box doesn't surface). Their value is still registered and
      // submitted; the server validates it authoritatively.
      if (field.operations.computed != null) return;
      // Hidden UI fields (worksheet intermediates) and disabled fields are
      // skipped for the same reason: the user has no input to fix. The server
      // treats disabled fields like hidden ones in validation.
      if (isHiddenUiField(field) || isFieldDisabled(field)) return;
      if (field.type === "SignatureBlock") hasSignatureField = true;
      const fieldSchema = buildFieldSchema(field, country, maskedIdValidator);
      if (fieldSchema) shape[field.key] = fieldSchema;
    });
  });

  if (!hasSignatureField && getAttestationText(metadata)) {
    shape.signature = Yup.object().shape({
      consentAccepted: Yup.boolean()
        .oneOf([true], "You must accept the attestation")
        .required("You must accept the attestation")
    });
  }

  return Yup.object().shape(shape);
};
